import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { rangeToFilterGroups } from '@app/src/pages/ResourceCollection/MrtCollection/filterBuilders'
import { summariseRange } from '@app/src/pages/ResourceCollection/MrtCollection/filters/filterValueSummary'
import type { MrtFilterOperator } from '@app/src/pages/ResourceCollection/MrtCollection/mrtCollectionTypes'
import type { Unit } from '@app/src/types/resourceExplorer'
import { parseCurrencyInput } from '@app/src/utils/currencyUtils'

/** What a spend selection asks about the period it names, one question at a time. */
export type SpendFilterMode = 'interval' | 'amount' | 'notAdded'

/**
 * The period selection that asks about every period at once rather than naming one. A sentinel rather
 * than an absent period, because absent already means "stored before the period was part of the
 * selection" - and the two answer "is not added" differently.
 */
export const ANY_PERIOD = 'anyPeriod'

/**
 * The spend selection, held as one value so every part describes the same spend rather than several
 * unrelated ones. The mode picks which of the three questions is being asked; normaliseSpendFilterValue
 * drops the parts belonging to the other two, so a selection only ever carries its own answer.
 */
export type SpendFilterValue = {
  periodName?: string
  mode?: SpendFilterMode
  spendClassifications?: string[]
  /** Raw editor input, parsed with parseCurrencyInput so '200k' and '1.5M' are accepted. */
  amountRange?: [string, string]
}

/** Where a collection's spends sit, e.g. 'provider.spends' or 'mappingNodes.provider.spends'. */
export type SpendFilterFields = { periodField: string; classificationField: string; amountField: string }

export const spendFilterFields = (spendPath: string): SpendFilterFields => ({
  periodField: `${spendPath}.periodName`,
  classificationField: `${spendPath}.spendClassification`,
  amountField: `${spendPath}.amount`,
})

export type SpendToFilterGroupsOptions = {
  /**
   * Narrows the rows a selection may match (the chain table only counts supplier relations). It joins a
   * selection and the "has any spend" question, but never "has no spend": rows without a spend cannot
   * satisfy a scope that requires one, which would contradict the question.
   */
  scopeGroups?: FilterGroup[]
  /** See normaliseSpendFilterValue: the period a bare [min, max] was stored against. */
  defaultPeriodName?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const asStrings = (value: unknown): string[] | undefined =>
  Array.isArray(value) && value.every(entry => typeof entry === 'string') ? (value as string[]) : undefined

const asMode = (value: unknown): SpendFilterMode | undefined =>
  value === 'interval' || value === 'amount' || value === 'notAdded' ? value : undefined

const toAmountRange = ([min, max]: string[]): [string, string] => [min ?? '', max ?? '']

/** The period a selection narrows to, which "any period" and an absent period alike do not have. */
const namedPeriod = (periodName?: string): string | undefined =>
  periodName && periodName !== ANY_PERIOD ? periodName : undefined

const parseBound = (raw: string): number | undefined => {
  const parsed = raw ? parseCurrencyInput(raw) : undefined
  return parsed === undefined || Number.isNaN(parsed) ? undefined : parsed
}

/**
 * The bounds that actually parse. The selection keeps the raw text, so the editor does not fight a
 * half-typed amount, but everything derived from it - whether it asks anything, the chip, the query -
 * reads these, and a bound that never parses is a bound nobody is filtering on.
 */
const parsedAmountBounds = (amountRange?: [string, string]): [number | undefined, number | undefined] => [
  parseBound(amountRange?.[0] ?? ''),
  parseBound(amountRange?.[1] ?? ''),
]

/**
 * The mode a value stored before the modes existed was asking for: the "is not added" switch, else the
 * part it actually filled in.
 */
const inferMode = (value: Record<string, unknown>): SpendFilterMode | undefined => {
  if (value.isNotAdded) return 'notAdded'
  if (asStrings(value.spendClassifications)?.length) return 'interval'
  return asStrings(value.amountRange)?.some(Boolean) ? 'amount' : undefined
}

/**
 * A stored filter value as the current shape, carrying only the chosen mode's own answer.
 *
 * Values persist in saved views and table state, so both earlier shapes are read back: the
 * switch-and-intervals object, and the plain [min, max] pair the provider tables stored while their
 * spend filter was a bare currency range. That range named no period because it was always the
 * organization's focused one, which is all `defaultPeriodName` supplies - a selection naming its own
 * period is left alone, and so is the switch, which meant "no spend at all" rather than "not the
 * focused period".
 */
export const normaliseSpendFilterValue = (value: unknown, defaultPeriodName?: string): SpendFilterValue | undefined => {
  const legacyRange = asStrings(value)
  if (legacyRange)
    return legacyRange.some(Boolean)
      ? { periodName: defaultPeriodName, mode: 'amount', amountRange: toAmountRange(legacyRange) }
      : undefined

  if (!isRecord(value)) return undefined

  const mode = asMode(value.mode) ?? inferMode(value)
  const amountRange = asStrings(value.amountRange)
  return {
    periodName: typeof value.periodName === 'string' ? value.periodName : undefined,
    mode,
    spendClassifications: mode === 'interval' ? asStrings(value.spendClassifications) : undefined,
    amountRange: mode === 'amount' && amountRange ? toAmountRange(amountRange) : undefined,
  }
}

/**
 * Whether the selection asks anything - a mode with its own answer filled in. A mode on its own narrows
 * nothing, so the editor withholds it, the query sends nothing and the chip stays blank.
 */
export const spendFilterAsksSomething = (selection: SpendFilterValue | undefined): boolean => {
  if (selection?.mode === 'notAdded') return true
  if (selection?.mode === 'interval') return Boolean(selection.spendClassifications?.length)
  return selection?.mode === 'amount' && parsedAmountBounds(selection.amountRange).some(bound => bound !== undefined)
}

/**
 * The selection as backend filter groups. Every group targets the same `spends` collection, so the
 * backend AND's them inside one `.Any()` - a single spend has to answer the whole selection, rather than
 * the period coming from one year and the amount from another.
 *
 * "Not added" negates the period test. Over any period there is no period to negate - nor in a selection
 * stored before the period was required - and both mean the same thing: no spend at all.
 */
export const createSpendToFilterGroups =
  (
    { periodField, classificationField, amountField }: SpendFilterFields,
    { scopeGroups = [], defaultPeriodName }: SpendToFilterGroupsOptions = {},
  ) =>
  (value: unknown, operator: MrtFilterOperator): FilterGroup[] => {
    const noSpendGroups: FilterGroup[] = [{ name: amountField, filters: [{ operator: Operators.IsEmpty }] }]
    if (operator === 'empty') return noSpendGroups
    if (operator === 'notEmpty')
      return [{ name: amountField, filters: [{ operator: Operators.IsNotEmpty }] }, ...scopeGroups]

    const selection = normaliseSpendFilterValue(value, defaultPeriodName)
    if (!selection || !spendFilterAsksSomething(selection)) return []

    const { periodName, mode, spendClassifications, amountRange } = selection
    const period = namedPeriod(periodName)
    if (mode === 'notAdded')
      return period
        ? [{ name: periodField, filters: [{ value: period, operator: Operators.NotEqualTo }] }, ...scopeGroups]
        : noSpendGroups

    const modeGroups =
      mode === 'interval'
        ? [{ name: classificationField, filters: [{ value: spendClassifications, operator: Operators.In }] }]
        : // Always 'is': the range is one side of a selection, not a filter with an operator of its own.
          rangeToFilterGroups(amountField, parseCurrencyInput)(amountRange, 'is')

    // An amount whose bounds are there but unparseable leaves nothing to ask, and a period on its own
    // narrows to whoever reported that year - which is not the question.
    if (!modeGroups.length) return []

    const periodGroups = period
      ? [{ name: periodField, filters: [{ value: period, operator: Operators.EqualTo }] }]
      : []
    return [...periodGroups, ...modeGroups, ...scopeGroups]
  }

export type SpendFilterSummaryOptions = {
  noSpendLabel: string
  orWord: string
  currencyUnit?: Unit
}

// Only the bounds that parse, so the chip describes what is actually being filtered on rather than what
// was typed.
const summariseAmount = (amountRange?: [string, string], currencyUnit?: Unit): string => {
  const bounds = parsedAmountBounds(amountRange).map(bound => (bound === undefined ? '' : String(bound)))
  return summariseRange(bounds, currencyUnit)
}

/** The chip's values: what the selection asks about its period, e.g. "High or Medium" or "No spend". */
export const summariseSpendFilter = (
  value: unknown,
  { noSpendLabel, orWord, currencyUnit }: SpendFilterSummaryOptions,
): string => {
  const selection = normaliseSpendFilterValue(value)
  if (!selection || !spendFilterAsksSomething(selection)) return ''
  if (selection.mode === 'notAdded') return noSpendLabel
  if (selection.mode === 'amount') return summariseAmount(selection.amountRange, currencyUnit)
  return (selection.spendClassifications ?? []).join(` ${orWord} `)
}

/**
 * The chip's subject: the column label and the period the selection is about, "Spend 2025", or
 * "Spend · Any period". A selection stored before the period was part of it asked about any period.
 */
export const spendChipSubject = (
  value: unknown,
  label: string,
  { anyPeriodLabel, defaultPeriodName }: { anyPeriodLabel: string; defaultPeriodName?: string },
): string => {
  const selection = normaliseSpendFilterValue(value, defaultPeriodName)
  if (!spendFilterAsksSomething(selection)) return label
  const period = namedPeriod(selection?.periodName)
  return period ? `${label} ${period}` : `${label} · ${anyPeriodLabel}`
}

/**
 * The period whose spend the column shows for a selection: the one it asks about, else the default.
 * "Not added" names the period nobody has spend in, so showing that one would only show blanks.
 */
export const spendDisplayPeriod = (value: unknown, defaultPeriodName?: string): string | undefined => {
  const selection = normaliseSpendFilterValue(value, defaultPeriodName)
  if (!selection || selection.mode === 'notAdded' || !spendFilterAsksSomething(selection)) return defaultPeriodName
  return namedPeriod(selection.periodName) ?? defaultPeriodName
}

export const NO_SPEND_COUNT_KEY = 'noSpend'

/**
 * The selections the spend group counts stand for, keyed by the option they count: each spend group on its
 * own, and no spend, all in the one period.
 */
export const spendGroupCountSelections = (
  periodName: string,
  spendClassifications: string[],
): { key: string; selection: SpendFilterValue }[] => [
  ...spendClassifications.map(classification => ({
    key: classification,
    selection: { periodName, mode: 'interval' as const, spendClassifications: [classification] },
  })),
  { key: NO_SPEND_COUNT_KEY, selection: { periodName, mode: 'notAdded' as const } },
]

/**
 * The period a committed selection asks for, read back off the groups it produced. A negated period
 * names the rows without it, so it is not a period anything can display.
 */
export const readSpendPeriod = (groups: FilterGroup[], periodField: string): string | undefined => {
  const [filter] = groups.find(group => group.name === periodField)?.filters ?? []
  if (filter?.operator !== Operators.EqualTo) return undefined
  return typeof filter.value === 'string' ? filter.value : undefined
}
